/**
 * InfoModal - Simple modal for metric explanations
 * 
 * Features:
 * - Plain English descriptions (2-4 bullet lines max)
 * - Optional "How it's calculated" section
 * - Keyboard accessible (ESC closes)
 * - Mobile-friendly
 */

import React, { useEffect, useCallback } from 'react';
import { X, Calculator, Lightbulb } from 'lucide-react';

export default function InfoModal({
  isOpen,
  onClose,
  title,
  description = [],
  calculation,
  whyItMatters,
}) {
  // Handle ESC key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="info-modal-backdrop"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="info-modal-title"
        data-testid="info-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-3 border-b border-slate-200 dark:border-slate-700">
          <h2 
            id="info-modal-title" 
            className="text-lg font-semibold text-gray-900 dark:text-white pr-4"
            data-testid="info-modal-title"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close"
            data-testid="info-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Description bullets */}
          <div className="space-y-2">
            {description.map((line, i) => (
              <p 
                key={i} 
                className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed flex items-start gap-2"
                data-testid="info-modal-description"
              >
                <span className="text-purple-500 dark:text-purple-400 mt-1">•</span>
                <span>{line}</span>
              </p>
            ))}
          </div>

          {/* Why it matters */}
          {whyItMatters && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-700/30">
              <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {whyItMatters}
              </p>
            </div>
          )}

          {/* Calculation */}
          {calculation && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/30">
              <Calculator className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  How it&apos;s calculated
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300 font-mono">
                  {calculation}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 rounded-xl transition-colors"
            data-testid="info-modal-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
